<script setup>
import fileUpload from 'vue-upload-component'
import filesList from './files-list.vue'
import filesHistory from "./files-history.vue";
import filesUploadList from "./files-upload-list.vue";
import fileStorePicker from "../filestore/file-store-picker.vue";
import {directUpload} from '@/lib/direct-upload'
import helpers from "@/lib/helpers";
import clipboard from '@/lib/photo-clipboard'
import {fileGroup} from './file-kind'
import {onMounted, onBeforeUnmount, ref, watch, computed, reactive} from 'vue';

const upload = ref()
const files = ref()
const uploads = ref([])
const mode = ref()
const emailing = ref()

const from_url = reactive({
  unzip: true,
  from_url: null
})

const props = defineProps({
  url: String,
  standalone: Boolean,
  noPublic: Boolean,
  write: String,
  /** offer Paste for photos on the photo clipboard: they go on as files (a message's attachments) */
  pastePhotos: Boolean,
  /** offer the file store's files (they go on by id, as a message's attachments) */
  fromStore: Boolean,
  /** just the files: no scan details, finder or type tabs (a message's attachments) */
  plain: Boolean,
  height: {
    type: String,
    default: '350px'
  }
})

const canPaste = computed(() => props.pastePhotos && clipboard.has.value)
const pasting = ref(false)

/**
 * The clipboard's photos onto the record as files, by id. Always a copy -
 * an attachment never takes a photo off the gallery it came from, so a cut
 * stays on the clipboard for the gallery it was meant for. The server says
 * no (and why) when the message would carry too many.
 */
const pastePhotos = async () => {
  if (!canPaste.value || pasting.value)
    return
  pasting.value = true
  try {
    const r = await helpers.asyncAction(props.url, 'POST', {data: {file_add: clipboard.photos.value.map(p => ({id: p.id}))}, noload: true})
    helpers.toast('Attached ' + r.files.length + ' photo' + (r.files.length === 1 ? '' : 's'))
    await refresh()
  } finally {
    pasting.value = false
  }
}

/** files picked out of the file store onto the record, by id */
const addFromStore = async (ids) => {
  const r = await helpers.asyncAction(props.url, 'POST', {data: {file_add: ids.map(id => ({id}))}, noload: true})
  helpers.toast('Attached ' + r.files.length + ' file' + (r.files.length === 1 ? '' : 's'))
  mode.value = false
  await refresh()
}

const emit = defineEmits(['refresh'])
// each file straight to storage (Google, when the files live there), so no size limit from our proxy
const uploadAction = computed(() => directUpload(props.url))

const gallery = ref(false)
const refresh = async (quiet = false) => {
  files.value = await helpers.asyncAction(props.url, 'GET', quiet ? {noload: true} : {})
  emit('refresh', files.value)
  watchReading()
}

onMounted(() => refresh())

// a file just added is read by the scans in the background: look again until it is (a minute or so at most)
let readingTimer = null
let readingTries = 0
const watchReading = () => {
  clearTimeout(readingTimer)
  if (!(files.value || []).some(f => f.scan?.status === 'pending') || readingTries >= 8) {
    readingTries = 0
    return
  }
  readingTries++
  readingTimer = setTimeout(() => refresh(true), 8000)
}
onBeforeUnmount(() => clearTimeout(readingTimer))

// --- finding a file: the filter (name, and what the scans read - summary, from, number, the text inside) and type tabs
const FILTER_FROM = 6
const query = ref('')
const matched = ref(null)
const group = ref('All')
let queryTimer = null
watch(query, (q) => {
  clearTimeout(queryTimer)
  if (!q.trim()) {
    matched.value = null
    return
  }
  queryTimer = setTimeout(async () => {
    const found = await helpers.asyncAction(props.url, 'GET', {data: {q: q.trim()}, noload: true})
    if (q === query.value)
      matched.value = new Set(found.map(f => f.id))
  }, 300)
})
const groups = computed(() => {
  const counts = {}
  for (const f of files.value || [])
    counts[fileGroup(f)] = (counts[fileGroup(f)] || 0) + 1
  // document types first (most first), then Photos, then Other
  const rank = (g) => g === 'Other' ? 2 : g === 'Photos' ? 1 : 0
  return Object.entries(counts).sort((a, b) => rank(a[0]) - rank(b[0]) || b[1] - a[1]).map(([name, count]) => ({name, count}))
})
watch(groups, (gs) => {
  if (group.value !== 'All' && !gs.find(g => g.name === group.value))
    group.value = 'All'
})
const showFinder = computed(() => !props.plain && (files.value || []).length >= FILTER_FROM)
const shown = computed(() => (files.value || []).filter(f =>
  (!matched.value || matched.value.has(f.id)) && (group.value === 'All' || fileGroup(f) === group.value)))

const has_images = computed(() => {
  return files.value && files.value.find((v) => v.is_image)
})

const cancelUpload = (upload_file) => {
  upload.value.update(upload_file, {error: 'cancel'})
  upload.value.remove(upload_file)
}

const clearMode = async () => {
  emailing.value = false
  from_url.from_url = null
  mode.value = false
  await refresh()
}

const email = async (body, files) => {
  emailing.value = await helpers.asyncAction(props.url, 'POST', {
    data: {
      email: true,
      body, files
    }
  })


  navigator.clipboard.writeText(emailing.value.token)
}

const do_from_url = async () => {
  try {
    await helpers.asyncAction(props.url, 'POST', {
      data: from_url
    })
    await refresh()
  } finally {
    // back to the list (use_from_url, which this set, never existed - a ReferenceError after every URL download)
    from_url.from_url = null
    mode.value = false
  }
}

const uploading = computed(() => Boolean(uploads.value.reduce((previousValue, currentValue) => previousValue + Number(currentValue.active), 0)))
const layout = computed(() => props.standalone ? 'toolbar-scroll' : 'modal')
watch(uploading, (newv) => {
  if (!newv) {
    refresh()
    history.value = false
  }
})
</script>

<template>
  <component :is="layout" title="Attached Files" :height="height" :hideTop="!!mode" large>
    <template #buttons v-if="!standalone">
      <b-button variant="link" class="text-white d-none d-sm-inline" size="sm" @click="clearMode" v-if="!!mode">
        BACK
      </b-button>
      <b-button variant="link" class="text-white d-none d-sm-inline" size="sm" @click="gallery = !gallery" v-else-if="!!has_images">
        {{ !gallery ? 'GALLERY' : 'LIST' }}
      </b-button>
    </template>

    <template #top v-if="(!write || can(write))">
      <div v-show="!mode">
        <!-- drop files anywhere on the panel, or click the strip to browse -->
        <div class="d-flex align-items-stretch">
          <file-upload
              class="upload-strip flex-fill"
              :class="{active: upload && upload.dropActive}"
              :custom-action="uploadAction"
              v-model="uploads"
              :multiple="true"
              :drop="true"
              :thread="3"
              @input-file="$refs.upload.active = true"
              ref="upload">
            <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" class="mr-1"/>
            <!-- one span: in the flex strip, loose text and the link are separate items and the space between them is lost -->
            <span v-if="upload && upload.dropActive">Drop to upload</span>
            <span v-else>Drop files here or <span class="text-primary">browse</span></span>
          </file-upload>
          <!-- the photos copied from a gallery, as attachments: there only while the clipboard has some -->
          <b-button v-if="canPaste" @click="pastePhotos" :disabled="uploading || pasting" variant="link" size="sm" class="text-secondary ml-1"
                    :title="'Attach the ' + clipboard.count.value + ' photo(s) on the clipboard'">
            <font-awesome-icon :icon="['far', 'paste']" class="mr-1"/>
            Paste {{ clipboard.count.value }}
          </b-button>
          <!-- the occasional ways in, behind one menu -->
          <b-dropdown :disabled="uploading" variant="link" size="sm" right no-caret class="ml-1"
                      toggle-class="text-secondary px-2 h-100" title="More ways to add files">
            <template #button-content>
              <font-awesome-icon icon="fa-solid fa-ellipsis"/>
            </template>
            <b-dropdown-item-button @click="mode = 'history'">
              <font-awesome-icon icon="fa-solid fa-history" fixed-width class="mr-1"/>
              Copy from a recent record
            </b-dropdown-item-button>
            <b-dropdown-item-button @click="mode = 'store'" v-if="fromStore && can('filestore.read')">
              <font-awesome-icon :icon="['fas', 'folder-open']" fixed-width class="mr-1"/>
              From the file store
            </b-dropdown-item-button>
            <b-dropdown-item-button @click="mode = 'url'" v-if="can('files.url')">
              <font-awesome-icon :icon="['fas', 'globe']" fixed-width class="mr-1"/>
              From a URL
            </b-dropdown-item-button>
            <b-dropdown-item-button @click="mode = 'email'" v-if="can('files.email')">
              <font-awesome-icon :icon="['fas', 'envelope']" fixed-width class="mr-1"/>
              Email them in
            </b-dropdown-item-button>
          </b-dropdown>
        </div>
        <div v-if="showFinder && !gallery" class="mt-2">
          <b-input-group size="sm">
            <b-input-group-prepend is-text>
              <font-awesome-icon icon="fa-solid fa-magnifying-glass"/>
            </b-input-group-prepend>
            <b-form-input v-model="query" type="search" placeholder="Find: name, number, words inside…" debounce="0"/>
          </b-input-group>
          <div v-if="groups.length > 1" class="file-groups mt-1">
            <b-badge v-for="g in [{name: 'All', count: (files || []).length}, ...groups]" :key="g.name" pill
                     :variant="group === g.name ? 'primary' : 'light'" class="mr-1 mb-1" role="button" @click="group = g.name">
              {{ g.name }} {{ g.count }}
            </b-badge>
          </div>
        </div>
      </div>
      <b-button v-show="!!mode" variant="link" @click="clearMode" class="py-0 pl-0" v-if="standalone">
        <font-awesome-icon icon="fa-solid fa-arrow-left"/>
        Go Back
      </b-button>
    </template>

    <files-history :url="url + '/recent'" v-if="mode === 'history'"/>

    <file-store-picker v-else-if="mode === 'store'" class="mx-2" @pick="addFromStore"/>

    <div v-else-if="mode === 'url'" class="mt-5 text-center">
      <b-card>
        <b-card-text>
          <b-form-input size="md" placeholder="URL" autofocus v-model="from_url.from_url"/>
          <b-form-checkbox v-model="from_url.unzip" switch class="my-3">Unzip Files</b-form-checkbox>
        </b-card-text>
        <b-button-group>
          <b-button variant="outline-primary" @click="do_from_url">
            Download
          </b-button>
          <b-button @click="clearMode" variant="outline-danger">
            Cancel
          </b-button>
        </b-button-group>
      </b-card>
    </div>


    <div class="mx-3" v-else-if="mode === 'email'">
      <b-card v-if="emailing instanceof Object">
        <b-card-header>
          Forward To
        </b-card-header>
        <b-card-text class="pt-3">
          <text-copy>{{ emailing.address }}</text-copy>
        </b-card-text>
        <b-card-header>
          Use Subject
        </b-card-header>
        <b-card-text class="pt-3">
          <text-copy>{{ emailing.token }}</text-copy>
        </b-card-text>
      </b-card>
      <div v-else class="w-100 mt-5 text-center">
        <b-button-group vertical>
          <b-button variant="outline-primary" @click="email(true, false)">
            Message Body
          </b-button>
          <b-button variant="outline-primary" @click="email(false, true)">
            Attachment Files
          </b-button>
          <b-button variant="outline-primary" @click="email(true, true)">
            Message Body + Files
          </b-button>
          <b-button variant="outline-danger" @click="clearMode">
            Cancel
          </b-button>
        </b-button-group>
      </div>
    </div>

    <photo-gallery v-else-if="gallery" :photos="files.filter(f => f.is_image)" not-photomap click-event/>
    <div v-else-if="!uploading && showFinder && files && !shown.length" class="text-muted text-center my-4">
      No file matches<template v-if="query"> "{{ query }}"</template>.
    </div>
    <files-list :no-public="noPublic" :plain="plain" v-else-if="!uploading" :files="shown" @refresh="refresh">
      <template #button="{file}">
        <slot name="button" :file="file" :refresh="refresh"/>
      </template>
      <template #menu="{file}">
        <slot name="menu" :file="file" :refresh="refresh"/>
      </template>
      <template #info="{file}">
        <slot name="info" :file="file" :refresh="refresh"/>
      </template>
      <template #more_info="{file}">
        <slot name="more_info" :file="file" :refresh="refresh"/>
      </template>
    </files-list>
    <files-upload-list v-else :uploads="uploads" @cancel="cancelUpload"/>
  </component>
</template>

<style scoped>
.upload-strip {
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: .35rem .75rem;
  border: 1px dashed #ced4da;
  border-radius: .25rem;
  color: #6c757d;
  font-size: .875rem;
  cursor: pointer;
  transition: background-color .15s, border-color .15s;
}

.upload-strip:hover, .upload-strip.active {
  border-color: #007bff;
  background: rgba(0, 123, 255, .04);
}
</style>